const focusableOptions = [
  // Interactive elements
  'button:not([disabled]):not([aria-disabled="true"]):not([aria-hidden="true"])',
  'a[href]:not([disabled]):not([aria-disabled="true"])',
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden="true"]):not([style*="display: none"]):not([name="cf-turnstile-response"]), input[name="cf-turnstile-response"]',
  'select:not([disabled]):not([aria-disabled="true"]):not([aria-hidden="true"])',
  'textarea:not([disabled]):not([aria-hidden="true"])',

  // Other interactive elements
  'details summary',
  'iframe:not([aria-hidden="true"])',
  'audio[controls]',
  'video[controls]',

  // Elements with explicit tab index
  '[contenteditable="true"]:not([aria-disabled="true"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled]):not([aria-disabled="true"]):not([aria-hidden="true"])',

  // Dialog-specific elements
  'dialog:not([aria-hidden="true"])',
  '[role="dialog"]:not([aria-hidden="true"])',

  // Custom elements that might be interactive
  '[role="button"]:not([disabled]):not([aria-disabled="true"])',
  '[role="link"]:not([disabled]):not([aria-disabled="true"])',
  '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])',
].join(', ');

// Children of an element with the inert prop shouldn't be focusable,
// we need to check if an ancestor of each focusable element has inert
// if yes, then the elements will be filtered out
const hasInertAncestor = (
  element: Element,
  root: Element | ShadowRoot,
): boolean => {
  let current: Element | null = element;
  while (current && current !== root) {
    if (current.hasAttribute('inert')) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
};

/**
 * Focuses `element`, giving it a temporary `tabindex="-1"` first if it isn't
 * already focusable (removed again once focus moves elsewhere). Most
 * non-interactive elements (`body`, a `<main>` landmark, a product tile,
 * ...) aren't focusable by default, so a plain `.focus()` call on them is a
 * silent no-op. A real, explicit focus call is also what resets the
 * browser's internal sequential-focus-navigation cursor to `element` —
 * `document.activeElement` already falls back there on its own in some
 * cases (e.g. once a previously focused element is removed), but that
 * fallback alone doesn't move the cursor, so the next Tab press would
 * continue from wherever it used to be instead of from `element`.
 */
export const focusWithFallbackTabIndex = (
  element: HTMLElement,
  focusOptions?: FocusOptions,
) => {
  const hadTabIndex = element.hasAttribute('tabindex');

  if (!hadTabIndex) {
    element.setAttribute('tabindex', '-1');
    element.addEventListener(
      'blur',
      () => element.removeAttribute('tabindex'),
      {once: true},
    );
  }

  element.focus(focusOptions);
};

/** Resets keyboard focus to the top of the document — see {@link focusWithFallbackTabIndex}. */
export const resetFocusToRoot = () =>
  focusWithFallbackTabIndex(document.body, {preventScroll: true});

export const getAllFocusableElements = (
  root: Element | ShadowRoot,
): HTMLElement[] => {
  const elements = [];

  if ('getAttribute' in root) {
    const rootTabindex = (root as HTMLElement).getAttribute('tabindex');
    if (rootTabindex) {
      elements.push(root as HTMLElement);
    }
  }

  // First get all potentially focusable elements
  const potentialElements = Array.from(
    root.querySelectorAll(focusableOptions),
  ).filter((el): el is HTMLElement => {
    if (!(el instanceof HTMLElement) || el.style.display === 'none') {
      return false;
    }
    return !hasInertAncestor(el, root);
  });

  elements.push(...potentialElements);

  // Handle shadow roots after filtering main elements
  const shadowRoots = Array.from(root.querySelectorAll('*'))
    .filter(el => el.shadowRoot)
    .map(el => el.shadowRoot as ShadowRoot);

  // Get focusable elements from each shadow root
  const shadowElements = shadowRoots.reduce(
    (acc: HTMLElement[], shadowRoot) => {
      return acc.concat(getAllFocusableElements(shadowRoot));
    },
    [],
  );

  return elements.concat(shadowElements);
};
